import * as React from "react";
import { cn } from "@/lib/utils";
import { statusLabel, type CompletionStatus } from "@/lib/copy";

/* StatusBadge — completion state, NOT risk band.
   Uses neutral symbols instead of coloured dots so completion status is
   not confused with performance classification colours. */

const statusSymbols: Record<CompletionStatus, string> = {
  completed: "✓",
  partial: "◐",
  notstarted: "○",
};

interface StatusBadgeProps extends React.HTMLAttributes<HTMLSpanElement> {
  status: CompletionStatus;
  size?: "sm" | "md";
}

export const StatusBadge = React.forwardRef<HTMLSpanElement, StatusBadgeProps>(
  ({ status, size = "md", className, ...props }, ref) => {
    const text = statusLabel(status);
    const symbol = statusSymbols[status];
    const sizeClasses =
      size === "sm" ? "text-xs px-2 py-0.5 gap-1.5" : "text-sm px-2.5 py-1 gap-2";

    return (
      <span
        ref={ref}
        role="status"
        aria-label={`Status: ${text}`}
        className={cn(
          "inline-flex items-center rounded-full font-bold border border-transparent bg-transparent text-[var(--fg-2)]",
          sizeClasses,
          className,
        )}
        {...props}
      >
        <span aria-hidden="true" className="inline-flex w-[1.1em] shrink-0 items-center justify-center font-bold leading-none">
          {symbol}
        </span>
        <span>{text}</span>
      </span>
    );
  },
);
StatusBadge.displayName = "StatusBadge";
